/**
 * DektiteCascadeInline (Iter256)
 * ──────────────────────────────
 * Mirrors the Penetration Pin's Kind / Usage / Ø cascade on the
 * Components.Dektite (formerly Aquaseal) section. Three fields:
 *
 *   ❶ Class — Std vs Retrofit pill toggle
 *   ❷ Kind  — Pipe vs Flue (dropdown)
 *   ❸ Ø     — Pipe diameter (number input, with common-Ø chips)
 *
 * On any field change the component calls `GET /api/component-rates/
 * dektite-snap` and stamps the resolved catalogue row onto the parent
 * line — `type_name`, `buy_cost`, `materials_rate`, `labour_rate`,
 * `contractor_rate`, `supplier`, `supplier_code`, plus the three new
 * cascade fields themselves so they round-trip on Save.
 *
 * Lives inline on every Dektite row above the BacktrayLinkInline strip.
 */
import React, { useEffect, useRef } from 'react';
import { toast } from 'sonner';

// Common Ø chips per kind — mirrors KIND_DIAS from cascadeHelpers.js so
// the picker feels familiar to operators coming from the annotator.
const KIND_DIAS = {
  pipe: [32, 40, 50, 80, 100],
  flue: [80, 100, 125, 140, 150, 160, 175, 240],
};

const CLASS_OPTS = [
  { v: 'std',      label: 'Std',      tip: 'Standard EPDM Dektite — fits over a clean pipe.' },
  { v: 'retrofit', label: 'Retrofit', tip: 'Cut + wrap Dektite — use when the pipe has fittings.' },
];

const KIND_OPTS = [
  { v: 'pipe', label: 'Pipe' },
  { v: 'flue', label: 'Flue' },
];

// UAT 2026-02 — Lead + Ali classes are surfaced when the appraisal's
// roof type is a tile / concrete family. For iron / long-run roofs
// only Std + Retrofit apply (metal Dektites). Ali is also valid on
// tin-tile roofs (future — the tin-tile builder consumes the same
// class list via its own cascade wrapper).
const isTileRoof = (ctx) => {
  const s = String(ctx || '').toLowerCase();
  return /concrete|clay|tile|decramastic|colortile/.test(s);
};
const isTinTileRoof = (ctx) => {
  const s = String(ctx || '').toLowerCase();
  return /tin\s*tile|decramastic|colortile/.test(s);
};

export default function DektiteCascadeInline({
  api,
  ref_id,
  line,
  onUpdate,
  roofTypeCtx = '',
}) {
  const dektiteClass = line.dektite_class || 'std';
  const pipeKind     = line.pipe_kind     || 'pipe';
  // Persisted as `pipe_diameter_mm` so the cascade survives a save and
  // also keeps the legacy `outlet_diameter_mm` field in lockstep
  // (used by BacktrayLinkInline's width-suggest math).
  const pipeDia      = Number(line.pipe_diameter_mm || line.outlet_diameter_mm || 0);
  // Roof-conditional class options. Std + Retrofit always visible for
  // continuity; Lead + Ali added on tile roofs (Concrete / Clay /
  // Decramastic / Colortile). Ali kept visible on tin-tile roofs too.
  const classOpts = [...CLASS_OPTS];
  if (isTileRoof(roofTypeCtx) || isTinTileRoof(roofTypeCtx)) {
    classOpts.push({ v: 'lead', label: 'Lead',
                     tip: 'Lead-based aquaseal — malleable sheet, fits any pipe shape on concrete/clay tile roofs.' });
    classOpts.push({ v: 'ali',  label: 'Ali',
                     tip: 'Aluminium-based aquaseal — soft-metal alternative to lead. Available on concrete/clay and tin-tile roofs.' });
  }

  // Throttle the snap request so the user can type Ø freely without a
  // round-trip on every keystroke. 250ms is enough that arrow-key
  // increments still feel snappy but a typed "80" doesn't fire 3 times.
  const snapTimer = useRef(null);

  const callSnap = async (dc, pk, dia, patch) => {
    if (!api) return;
    if (!dia || dia <= 0) {
      onUpdate?.({ ...patch });
      return;
    }
    try {
      const { data } = await api.get('/component-rates/dektite-snap', {
        params: {
          dektite_class:    dc,
          pipe_kind:        pk,
          pipe_diameter_mm: dia,
        },
      });
      const rate = data?.rate;
      const warning = data?.warning || '';
      const matchFlag = data?.match || 'none';
      const stamp = {};
      if (rate) {
        stamp.type_name      = rate.type_name || '';
        stamp.buy_cost       = Number(rate.buy_cost)        || 0;
        stamp.materials_rate = Number(rate.materials_rate)  || 0;
        stamp.labour_rate    = Number(rate.labour_rate)     || 0;
        stamp.contractor_rate= Number(rate.contractor_rate) || 0;
        stamp.supplier       = rate.supplier || '';
        stamp.supplier_code  = rate.supplier_code || '';
        stamp.materials_desc = rate.materials_desc || '';
        stamp.labour_details = rate.labour_details || '';
        stamp.override       = false;
        if (matchFlag === 'closest' && warning) {
          toast.info(`${ref_id}: ${warning}`);
        }
      }
      onUpdate?.({ ...patch, ...stamp });
    } catch (e) {
      // Best-effort — still persist the cascade fields so user input
      // isn't lost. Surface the error once.
      // eslint-disable-next-line no-console
      console.warn('dektite-snap failed', e?.response?.data || e?.message);
      onUpdate?.({ ...patch });
    }
  };

  const applyClass = (v) => {
    const patch = { dektite_class: v };
    callSnap(v, pipeKind, pipeDia, patch);
  };
  const applyKind = (v) => {
    // Default Ø sensibly when switching kind from a Ø that has no
    // home on the new kind's chip list (e.g. switching pipe→flue from
    // Ø32 → bump to Ø80 so the user sees a usable starting size).
    const nextDia = (KIND_DIAS[v] || []).includes(pipeDia) ? pipeDia
                  : (v === 'flue' ? 160 : 50);
    const patch = { pipe_kind: v, pipe_diameter_mm: nextDia, outlet_diameter_mm: nextDia };
    callSnap(dektiteClass, v, nextDia, patch);
  };
  const applyDia = (d) => {
    const n = Number(d) || 0;
    const patch = { pipe_diameter_mm: n, outlet_diameter_mm: n };
    if (snapTimer.current) clearTimeout(snapTimer.current);
    snapTimer.current = setTimeout(() => callSnap(dektiteClass, pipeKind, n, patch), 250);
    // Mirror the input state immediately even before the snap fires —
    // the timer above will overwrite with rate fields once resolved.
    onUpdate?.(patch);
  };

  // Cleanup any pending throttle on unmount.
  useEffect(() => () => { if (snapTimer.current) clearTimeout(snapTimer.current); }, []);

  const dias = KIND_DIAS[pipeKind] || [];

  return (
    <div
      className="col-span-12 w-full"
      data-testid={`dektite-cascade-${ref_id}`}
    >
    <div
      className="mt-2 p-2 bg-teal-50/70 ring-1 ring-teal-300 rounded"
    >
      <div className="text-[10px] uppercase tracking-wider font-bold text-teal-800 mb-2">
        Dektite — cascade
      </div>

      {/* Iter256 — three-column inline grid so Class / Kind / Ø stay
          on a single row at desktop widths. Collapses to stacked on
          narrow screens. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {/* ❶ Class */}
      <div>
        <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">
          ❶ Class
        </label>
        <div className="flex gap-1.5 flex-wrap" data-testid={`dektite-cascade-class-row-${ref_id}`}>
          {classOpts.map(opt => (
            <button
              key={opt.v}
              type="button"
              data-testid={`dektite-cascade-class-${opt.v}-${ref_id}`}
              onClick={() => applyClass(opt.v)}
              title={opt.tip}
              className={`flex-1 px-2 py-1 rounded text-[11px] font-semibold ring-1 transition-colors ${
                dektiteClass === opt.v
                  ? (opt.v === 'lead' || opt.v === 'ali' ? 'bg-zinc-700 text-white ring-zinc-700' : 'bg-teal-600 text-white ring-teal-600')
                  : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
              }`}
            >{opt.label}</button>
          ))}
        </div>
      </div>

      {/* ❷ Kind */}
      <div>
        <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">
          ❷ Kind
        </label>
        <select
          data-testid={`dektite-cascade-kind-${ref_id}`}
          value={pipeKind}
          onChange={e => applyKind(e.target.value)}
          className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs bg-white"
        >
          {KIND_OPTS.map(opt => (
            <option key={opt.v} value={opt.v}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* ❸ Ø */}
      <div>
        <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">
          ❸ Ø Pipe diameter <span className="text-slate-400 font-normal normal-case">— mm</span>
        </label>
        <input
          type="number"
          min="0"
          step="1"
          value={pipeDia || ''}
          onChange={e => applyDia(e.target.value)}
          placeholder="Ø mm"
          data-testid={`dektite-cascade-dia-input-${ref_id}`}
          className="w-full px-2 py-1 border border-slate-200 rounded text-xs font-mono-num bg-white"
        />
      </div>
      </div>

      {/* Ø quick-pick chips on a second row, full width so the chip
          set has room to breathe regardless of the parent grid. */}
      <div className="flex flex-wrap items-center gap-1 mt-2">
        <span className="text-[9px] uppercase font-bold tracking-wider text-slate-500 mr-1">Common Ø</span>
        {dias.map(d => (
          <button
            key={d}
            type="button"
            data-testid={`dektite-cascade-dia-${d}-${ref_id}`}
            onClick={() => applyDia(d)}
            className={`px-1.5 py-0.5 text-[10px] font-mono-num rounded ring-1 transition-colors ${
              pipeDia === d
                ? 'bg-teal-600 text-white ring-teal-600'
                : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
            }`}
          >{d}</button>
        ))}
      </div>

      {line.type_name && (
        <div
          data-testid={`dektite-cascade-resolved-${ref_id}`}
          className="mt-2 text-[11px] text-emerald-800 bg-emerald-50 ring-1 ring-emerald-200 rounded px-2 py-1"
        >
          <span className="font-bold">✓ {line.type_name}</span>
          {line.supplier_code && (
            <span className="ml-1 text-[10px] font-mono-num text-emerald-700">{line.supplier_code}</span>
          )}
        </div>
      )}
    </div>
    </div>
  );
}
